/**
 * スレッド一覧コンポーネント
 * メッセージスレッドの一覧表示と操作
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Skeleton,
  Pagination,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  AccountCircle as AccountCircleIcon,
  Apartment as ApartmentIcon,
  Archive as ArchiveIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import {
  MessageThread,
  ThreadStatus,
  SenderType,
} from '../../types/message-thread';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';

interface ThreadListProps {
  threads: MessageThread[];
  selectedThreadId: string | null;
  onSelectThread: (threadId: string) => void;
  loading: boolean;
  userType: 'company' | 'student';
  onArchive?: (threadId: string) => void;
  onBlock?: (threadId: string) => void;
  onActivate?: (threadId: string) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onNextPage: () => void;
  onPrevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  selectedThreadId,
  onSelectThread,
  loading,
  userType,
  onArchive,
  onBlock,
  onActivate,
  pagination,
  onNextPage,
  onPrevPage,
  hasNextPage,
  hasPrevPage,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(
    null
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    thread: MessageThread
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedThread(thread);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedThread(null);
  };

  const handleMenuAction = (action: 'archive' | 'block' | 'activate') => {
    if (!selectedThread) return;

    switch (action) {
      case 'archive':
        onArchive?.(selectedThread.id);
        break;
      case 'block':
        // 学生のみブロック機能を使用
        if (userType === 'student') {
          onBlock?.(selectedThread.id);
        }
        break;
      case 'activate':
        onActivate?.(selectedThread.id);
        break;
    }
    handleMenuClose();
  };

  // 実際の表示用ステータスを計算（学生・企業の個別フラグを考慮）
  const getDisplayStatus = (thread: MessageThread) => {
    if (userType === 'student') {
      if (thread.studentBlocked) return ThreadStatus.BLOCKED;
      if (thread.studentArchived) return ThreadStatus.ARCHIVED;
    } else {
      if (thread.companyArchived) return ThreadStatus.ARCHIVED;
    }
    return thread.status;
  };

  const getStatusColor = (thread: MessageThread) => {
    const displayStatus = getDisplayStatus(thread);
    switch (displayStatus) {
      case ThreadStatus.ACTIVE:
        return 'success';
      case ThreadStatus.ARCHIVED:
        return 'default';
      case ThreadStatus.BLOCKED:
        return 'error';
      case ThreadStatus.CLOSED:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (thread: MessageThread) => {
    const displayStatus = getDisplayStatus(thread);
    switch (displayStatus) {
      case ThreadStatus.ACTIVE:
        return 'アクティブ';
      case ThreadStatus.ARCHIVED:
        return 'アーカイブ';
      case ThreadStatus.BLOCKED:
        return 'ブロック';
      case ThreadStatus.CLOSED:
        return 'クローズ';
      default:
        return displayStatus;
    }
  };

  const formatLastMessageTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ja,
      });
    } catch {
      return '不明';
    }
  };

  if (loading && threads.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" height={80} />
          </Box>
        ))}
      </Box>
    );
  }

  if (threads.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1">メッセージスレッドがありません</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* スレッドリスト */}
      <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {threads.map((thread, index) => (
          <React.Fragment key={thread.id}>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedThreadId === thread.id}
                onClick={() => onSelectThread(thread.id)}
                sx={{
                  py: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.50',
                    borderRight: 3,
                    borderRightColor: 'primary.main',
                  },
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={thread.unreadCount || 0}
                    color="error"
                    invisible={!thread.unreadCount}
                  >
                    <Avatar
                      src={
                        userType === 'company'
                          ? thread.student?.avatarUrl || undefined
                          : thread.company?.logoUrl || undefined
                      }
                      alt={
                        userType === 'company'
                          ? thread.student?.name || '学生'
                          : thread.company?.name || '企業'
                      }
                      sx={{ bgcolor: 'grey.100' }}
                    >
                      {userType === 'company' ? (
                        <AccountCircleIcon sx={{ color: 'grey.500' }} />
                      ) : (
                        <ApartmentIcon sx={{ color: 'grey.500' }} />
                      )}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: thread.unreadCount ? 'bold' : 'normal',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {thread.subject}
                      </Typography>
                      <Chip
                        size="small"
                        label={getStatusText(thread)}
                        color={getStatusColor(thread)}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: thread.unreadCount ? 'medium' : 'normal',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 0.5,
                        }}
                      >
                        {userType === 'company'
                          ? thread.student?.name || '学生'
                          : thread.company?.name || '企業'}
                      </Typography>
                      {thread.latestMessage && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          {thread.latestMessage.preview}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {formatLastMessageTime(thread.lastMessageAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {thread.messageCount}件
                        </Typography>
                      </Box>
                    </Box>
                  }
                />

                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, thread)}
                  sx={{ ml: 1 }}
                >
                  <MoreVertIcon />
                </IconButton>
              </ListItemButton>
            </ListItem>
            {index < threads.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      {/* ページネーション */}
      {pagination.totalPages > 1 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={(_, page) => {
              if (page > pagination.page) {
                onNextPage();
              } else if (page < pagination.page) {
                onPrevPage();
              }
            }}
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* コンテキストメニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
      >
        {selectedThread &&
          getDisplayStatus(selectedThread) === ThreadStatus.ACTIVE && (
            <MenuItem onClick={() => handleMenuAction('archive')}>
              <ArchiveIcon sx={{ mr: 1 }} />
              アーカイブ
            </MenuItem>
          )}
        {selectedThread &&
          getDisplayStatus(selectedThread) === ThreadStatus.ACTIVE &&
          userType === 'student' && (
            <MenuItem onClick={() => handleMenuAction('block')}>
              <BlockIcon sx={{ mr: 1 }} />
              ブロック
            </MenuItem>
          )}
        {selectedThread &&
          getDisplayStatus(selectedThread) !== ThreadStatus.ACTIVE && (
            <MenuItem onClick={() => handleMenuAction('activate')}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              アクティブに戻す
            </MenuItem>
          )}
      </Menu>
    </Box>
  );
};
